import React from "react";
import { motion } from "framer-motion";
import { BottomSheet } from "@/components/ui/bottom-sheet";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";

interface PrivacySheetProps {
  isOpen: boolean;
  onClose: () => void;
}

export const privacySections = [
  {
    title: "Information We Collect",
    items: [
      {
        question: "What personal information do you collect?",
        answer: "We may collect personal information such as: name, email, phone number, and shipping address when you place an order, payment details (processed securely through third-party payment providers), and browsing data, preferences, and interactions with our website."
      }
    ]
  },
  {
    title: "How We Use Your Information",
    items: [
      {
        question: "How do you use my data?",
        answer: "We use your data to: process and deliver your orders, provide customer support and respond to inquiries, improve our website and shopping experience, and send promotional updates (if you opt in)."
      }
    ]
  },
  {
    title: "Data Protection and Security",
    items: [
      {
        question: "How do you protect my data?",
        answer: "We implement strict security measures to protect your personal information from unauthorized access, misuse, or disclosure."
      }
    ]
  },
  {
    title: "Sharing Your Information",
    items: [
      {
        question: "Who do you share my data with?",
        answer: "We do not sell or trade your personal data. However, we may share it with trusted third parties, such as: shipping carriers to deliver your orders, payment processors for secure transactions, and legal authorities if required by law."
      }
    ]
  },
  {
    title: "Cookies and Tracking",
    items: [
      {
        question: "How do you use cookies?",
        answer: "Our website uses cookies to enhance user experience, track analytics, and personalize content. You can manage cookie preferences through your browser settings."
      }
    ]
  },
  {
    title: "Your Rights",
    items: [
      {
        question: "What are my privacy rights?",
        answer: "You have the right to: access, update, or delete your personal information, opt out of marketing emails at any time, and request details on how we use your data."
      }
    ]
  },
  {
    title: "Policy Updates",
    items: [
      {
        question: "How will I know about policy changes?",
        answer: "We may update this Privacy Policy as needed. Any changes will be posted on our website."
      }
    ]
  },
  {
    title: "Contact Information",
    items: [
      {
        question: "How can I contact you about privacy concerns?",
        answer: "For any privacy-related inquiries, please contact us at: 📧 help@teluasegh.com or 📞 01044916655"
      }
    ]
  }
];

export function PrivacySheet({ isOpen, onClose }: PrivacySheetProps) {
  return (
    <BottomSheet isOpen={isOpen} onClose={onClose}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        className="p-6"
      >
        <div className="flex items-center justify-center mb-6">
          <h2 className="text-2xl font-semibold">Privacy Policy</h2>
        </div>
        <div className="space-y-6">
          <p className="text-muted-foreground">
            At TEL U ASEGH, we respect your privacy and are committed to protecting your personal information. 
            This policy explains how we collect, use, and safeguard your data when you interact with our website and services.
          </p>
          <Accordion type="single" collapsible className="w-full">
            {privacySections.map((section, index) => (
              <div key={section.title} className="mb-6">
                <h3 className="text-lg font-medium mb-4">{section.title}</h3>
                {section.items.map((item, itemIndex) => (
                  <AccordionItem key={itemIndex} value={`${index}-${itemIndex}`}>
                    <AccordionTrigger className="text-left">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent>{item.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </div>
            ))}
          </Accordion>
          
          <div className="flex justify-center mt-8">
            <Button 
              type="button"
              variant="default" 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClose();
              }}
              className="px-8"
            >
              Close
            </Button>
          </div>
        </div>
      </motion.div>
    </BottomSheet>
  );
} 